import {Deal, DealCategory, PaymentCurrencyType} from "@irati/types"
import platformTracking from "@irati-tracking/platform/platform"
import {getProductCurrencyByDeal} from "@irati/deals/helpers/deals/currency"
import service from "@irati-tracking/tracker"

const dynamicTrackingBasedOnCurrency = (currency: string | null) => {
  return currency === PaymentCurrencyType.Rp ? service.trackRewardEvent : service.trackPerksEvent
}

class PerksTrackingEvents {
  viewHome() {
    service.trackPerksEvent("perks_view_home")
  }

  clickBrowseByCategory() {
    service.trackPerksEvent("perks_click_browse_by_category")
  }

  viewPerk(deal: Deal, entry?: string) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_type: deal.type,
      perk_category: deal.tagIds,
      earn_points: deal.earnPoints,
      perk_currency: getProductCurrencyByDeal(deal),
      deal_provider: deal.provider,
      entry
    }

    const dynamicTracking = dynamicTrackingBasedOnCurrency(data.perk_currency)
    dynamicTracking("perks_view_perk", data)
  }

  closePerk(deal: Deal, screenName: string) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_type: deal.type,
      perk_category: deal.tagIds,
      earn_points: deal.earnPoints,
      perk_currency: getProductCurrencyByDeal(deal)
    }
    platformTracking.dismiss("deal", screenName, data)
  }

  favouritePerk(deal: Deal, location: string) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_category: deal.tags,
      action_location: location
    }
    service.trackPerksEvent("perks_favourite_perk", data)
  }

  unfavouritePerk(deal: Deal, location: string) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_category: deal.tagIds,
      action_location: location
    }
    service.trackPerksEvent("perks_unfavourite_perk", data)
  }

  viewTerms(deal: Deal, isRewards = false) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_category: deal.tagIds,
      isRewards
    }
    service.trackPerksEvent("perks_view_terms", data)
  }

  startRedeemPerk(deal: Deal) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_type: deal.type,
      perk_type_highlight: deal.typeHighlight,
      perk_category: deal.tagIds,
      perk_currency: getProductCurrencyByDeal(deal),
      deal_provider: deal.provider
    }

    const dynamicTracking = dynamicTrackingBasedOnCurrency(data.perk_currency)
    dynamicTracking("perks_start_redeem_perk", data)
  }

  redeemPerkFinishSuccess(deal: Deal) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_type: deal.type,
      perk_type_highlight: deal.typeHighlight,
      perk_category: deal.tagIds,
      perk_currency: getProductCurrencyByDeal(deal),
      deal_provider: deal.provider
    }

    const dynamicTracking = dynamicTrackingBasedOnCurrency(data.perk_currency)
    dynamicTracking("perks_finish_redeem_deal_success", data)
  }

  redeemPerkStartCheckout(deal: Deal) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_type: deal.type,
      perk_type_highlight: deal.typeHighlight,
      perk_category: deal.tagIds,
      perk_currency: getProductCurrencyByDeal(deal),
      deal_provider: deal.provider
    }

    const dynamicTracking = dynamicTrackingBasedOnCurrency(data.perk_currency)
    dynamicTracking("perks_redeem_perk_start_checkout", data)
  }

  redeemPerkOrderProduct(deal: Deal) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_type: deal.type,
      perk_type_highlight: deal.typeHighlight,
      perk_category: deal.tagIds,
      perk_currency: getProductCurrencyByDeal(deal)
    }

    service.trackPerksEvent("perks_redeem_perk_order_product", data)
  }

  redeemPerkCopyCode(deal: Deal) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_type: deal.type
    }
    service.trackPerksEvent("perks_redeem_perk_copy_code", data)
  }

  redeemPerkFollowLink(deal: Deal) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_type: deal.type
    }
    service.trackPerksEvent("perks_redeem_perk_follow_link", data)
  }

  redeemPerkCompleteCheckout(deal: Deal, amount: number, currency: string, walletSpend: number) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_type: deal.type,
      perk_type_highlight: deal.typeHighlight,
      perk_category: deal.tagIds,
      perk_currency: currency,
      deal_provider: deal.provider,
      wallet_used: !!walletSpend,
      wallet_used_balance: walletSpend || null,
      transaction_amount: amount
    }

    const dynamicTracking = dynamicTrackingBasedOnCurrency(currency)
    dynamicTracking("perks_redeem_perk_complete_checkout", data)
  }

  redeemBirthdayBoxPerkComplete(dateOfBirth: string) {
    const data = {
      user_date_of_birth: dateOfBirth
    }
    service.trackPerksEvent("perks_redeem_birthday_box", data)
  }

  clickFilter(product: "perks" | "rewards", type = "filter") {
    const data = {
      type
    }
    const dynamicTracking = product === "rewards" ? service.trackRewardEvent : service.trackPerksEvent
    dynamicTracking("filter_buttonclick", data)
  }

  perksStartSearch(product: "perks" | "rewards", filtersApplied = false, sortApplied = false) {
    const data = {
      filtersApplied,
      sortApplied
    }
    const dynamicTracking = product === "rewards" ? service.trackRewardEvent : service.trackPerksEvent
    dynamicTracking("perks_start_search", data)
  }

  // NOTE: The perks_click_homepage_perk event is now used for any click on deal cards
  clickPerk(deal: Deal, trackingData: any) {
    const {sectionHeader, position, userLocation, source} = trackingData
    const currency = getProductCurrencyByDeal(deal)
    const {location} = deal

    interface LooseObject {
      [key: string]: any
    }

    const data: LooseObject = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_category: deal.tagIds,
      perk_type: deal.type,
      deal_provider: deal.provider,
      source
    }

    if (location) {
      data.perk_location = location.city
    }
    if (userLocation) {
      data.user_location = userLocation
    }
    if (source === "homepage") {
      data.lane_name = sectionHeader
      data.perk_position = position
    }

    if (currency === PaymentCurrencyType.Rp) {
      service.trackRewardEvent("perks_click_homepage_perk", data)
    } else {
      service.trackPerksEvent("perks_click_homepage_perk", data)
    }
  }

  viewAllDeals(isRewards = false) {
    const data = {
      isRewards
    }
    service.trackPerksEvent("perks_view_all_deals", data)
  }

  viewCategory(categoryName: string, isRewards = false, source?: string) {
    const data = {
      category_name: categoryName,
      isRewards,
      source
    }
    service.trackPerksEvent("perks_view_category", data)
  }

  viewTag(tagName: string) {
    const data = {tag_name: tagName}
    service.trackPerksEvent("perks_view_tag", data)
  }

  perksCategoryExperiment(experimentName: string, experimentChoice: string) {
    const data = {
      experiment_name: experimentName,
      experiment_choice_received: experimentChoice
    }
    service.trackPerksEvent("perks_category_experiment", data)
  }

  markPerkUsed(deal: Deal) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header
    }
    service.trackPerksEvent("perks_mark_perk_used", data)
  }

  viewZipModal() {
    service.trackPerksEvent("perks_view_zip_modal")
  }

  moreAboutZip() {
    service.trackPerksEvent("perks_more_about_zip")
  }

  paymentMethodSelected(paymentMethod: string) {
    const data = {
      payment_method: paymentMethod
    }
    service.trackPerksEvent("perks_payment_method", data)
  }

  topCategoryTileClicked({name, uuid}: DealCategory, index: number) {
    const data = {
      category_id: uuid,
      category_name: name,
      lane_name: "top categories"
    }
    service.trackPerksEvent(`top_categories_tile${index + 1}_clicked`, data)
  }

  visitRedirectCouponSite() {
    service.trackPerksEvent("visit_redirect_coupon_site")
  }

  clickCouponSite() {
    service.trackPerksEvent("click_coupon_site")
  }

  clickGiftCardSite(link: string) {
    const data = {
      affiliate_link: link
    }
    service.trackPerksEvent("click_gift_card_site", data)
  }

  clickGetFlexiPointsOption(deal: Deal) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_type: deal.type,
      perk_category: deal.tagIds
    }
    service.trackPerksEvent("earn_click_selectearn_product", data)
  }

  clickGetDiscountOption(deal: Deal) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_type: deal.type,
      perk_category: deal.tagIds
    }
    service.trackPerksEvent("earn_click_selectdiscount_product", data)
  }

  clickContinueWithDiscountOption(deal: Deal) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_type: deal.type,
      perk_category: deal.tagIds
    }
    service.trackPerksEvent("earn_click_continuediscount_product", data)
  }

  clickContinueWithFlexiPointsOption(deal: Deal) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_type: deal.type,
      perk_category: deal.tagIds
    }
    service.trackPerksEvent("earn_click_continueearn_product", data)
  }

  hoverCashbackTooltip(deal: Deal) {
    const data = {
      perk_id: deal.uuid,
      perk_name: deal.header,
      perk_slug: deal.urlSlug,
      perk_type: deal.type,
      perk_category: deal.tagIds
    }
    service.trackPerksEvent("earn_click_tooltip_select_product", data)
  }

  checkoutFailure(data: any) {
    service.trackPerksEvent("checkout_failure", data)
  }

  clickBrowseLinkBanner(product: "perks" | "wellness") {
    if (product === "perks") {
      service.trackPerksEvent("click_flexi_point_banner_v2_flexi_perks")
    } else if (product === "wellness") {
      service.trackPerksEvent("click_flexi_point_banner_v2_wellness")
    }
  }

  perksViewCatalog = (
    product: "perks" | "rewards",
    category_name?: string,
    sortApplied?: boolean,
    filterApplied?: boolean,
    type?: string
  ) => {
    const dynamicTracking = product === "rewards" ? service.trackRewardEvent : service.trackPerksEvent

    const data = {
      category_name,
      ...(typeof sortApplied === "boolean" && {sort_applied: sortApplied}),
      ...(typeof filterApplied === "boolean" && {filter_applied: filterApplied}),
      ...(type && {type})
    }
    dynamicTracking("view_catalog", data)
  }

  clickMyPerks = (source: "header" | "menu" | "wallet_transaction" | "redeem_success") => {
    service.trackEvent("platform_click_order_history", {source})
  }

  viewMyPerks = () => {
    service.trackEvent("platform_view_order_history")
  }

  viewBrand(id: string, label?: string) {
    const data = {
      id,
      label
    }

    service.trackPerksEvent("view_brand_page", data)
  }

  clickBrand = (data: {label: string; id: string; source: string}) => {
    service.trackPerksEvent("click_brand", data)
  }
}

export default new PerksTrackingEvents()
